/**
 * MaterialRequestCard Component
 *
 * Card component to display a material request in history.
 * Shows status with color coding and cancel action for pending requests.
 *
 * @module pages/technician/materials/MaterialRequestCard
 */

import React, { useState } from 'react';
import { Card, Button, Modal, Spinner, Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { formatDate, formatDateTime } from '../../../utils/formatters';
import {
  MATERIAL_REQUEST_STATUS,
  MATERIAL_REQUEST_STATUS_LABELS,
} from '../../../utils/constants';
import { cancelMaterialRequest } from '../../../services/materialRequestService';
import { toast } from '../../../utils/toast';
import KebabMenu from '../../../components/common/KebabMenu';
import PropTypes from 'prop-types';

const MaterialRequestCard = ({ request, onCancelled }) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const { isTechnician } = useAuth();

  // A technician who follows `/proyectos/:id` or `/tareas/:id` is bounced by
  // PrivateLayout (those paths are not in `technicianAllowedPaths`) and lands on
  // /tecnico/dashboard instead of the thing they clicked. Point them at the
  // equivalent screen inside their own portal.
  const projectPath = (id) => (isTechnician ? `/tecnico/proyectos/${id}` : `/proyectos/${id}`);
  const taskPath = (id) => (isTechnician ? `/tecnico/tareas/${id}` : `/tareas/${id}`);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case MATERIAL_REQUEST_STATUS.PENDING:
        return 'pending';
      case MATERIAL_REQUEST_STATUS.APPROVED:
        return 'approved';
      case MATERIAL_REQUEST_STATUS.REJECTED:
        return 'rejected';
      case MATERIAL_REQUEST_STATUS.CANCELLED:
        return 'cancelled';
      case MATERIAL_REQUEST_STATUS.DELIVERED:
        return 'delivered';
      default:
        return '';
    }
  };

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      const { data, error } = await cancelMaterialRequest(request.id);
      if (error) {
        console.error('Cancel error:', error);
        toast.error('Error al cancelar la solicitud');
      } else {
        toast.success('Solicitud cancelada');
        setShowCancelModal(false);
        onCancelled?.(data);
      }
    } catch (err) {
      console.error('Cancel error:', err);
      toast.error('Error al cancelar la solicitud');
    } finally {
      setIsCancelling(false);
    }
  };

  const isPending = request.status === MATERIAL_REQUEST_STATUS.PENDING;
  // The column is `quantity_requested` (`material_requests`); there is no
  // `quantity`. Reading the wrong name printed an empty string, so every card
  // said "  unidades" and the cancel modal asked "¿cancelar la solicitud de
  // unidades de X?".
  const quantity = request.quantity_requested;
  // `unit` is NOT NULL on the request itself — the catalogue item is only a
  // fallback for rows fetched without the join (and for external requests there
  // is no inventory item at all).
  const unitLabel = request.unit || request.inventory_item?.unit || 'unidades';

  // ── Solicitud de VARIOS materiales: la cabecera no es el material (#356) ──
  //
  // Una solicitud dictada ("ocupo 4 ductos, 2 codos y cinta") se guarda como
  // UNA fila cabecera en `material_requests` más N en `material_request_items`.
  // Esa cabecera tiene `quantity_requested` y `unit` NOT NULL y no hay nada
  // sensato que poner ahí, así que el escritor pone `1` / `'varios'` — un
  // relleno estructural, no un dato.
  //
  // Esta tarjeta lo renderizaba crudo. Medido en producción: 42 solicitudes
  // multi-ítem, LAS 42 sin `inventory_id` ni `external_description`, o sea que
  // la tarjeta decía literalmente **"1 varios de Material desconocido"** — la
  // maquinaria del contenedor, mostrada como si fuera lo pedido.
  //
  // La bandeja del admin y el panel del supervisor ya lo hacían bien: rendean
  // el bloque de ítems y dejan la cabecera para el caso legacy de un solo
  // material. Esta pantalla se quedó atrás, y es justo la del técnico — el que
  // dicta. Es la deuda de paridad de siempre, esta vez entre dos pantallas.
  const items = Array.isArray(request.items) ? request.items : [];
  const esMultiItem = items.length > 1;

  const materialName = esMultiItem
    ? `${items.length} materiales`
    : (items[0]?.material_name
      || request.inventory_item?.name
      || request.external_description
      || 'Material desconocido');

  /** Los primeros materiales, para reconocer la solicitud de un vistazo. */
  const resumenItems = (() => {
    if (!esMultiItem) return null;
    const visibles = items.slice(0, 3).map((i) => i.material_name).filter(Boolean);
    const restantes = items.length - visibles.length;
    // Si ningún ítem tiene nombre no se inventa nada: se dice cuántos son.
    if (visibles.length === 0) return `${items.length} materiales`;
    return restantes > 0 ? `${visibles.join(', ')} y ${restantes} más` : visibles.join(', ');
  })();
  // `material_requests` has no `priority` column, so `request.priority` was
  // always undefined and the "Urgente" flag could never light up. Since the
  // #P0-D1 fix, MaterialRequestForm writes urgency into `notes` as the literal
  // "URGENTE" marker — read the same convention the writer uses.
  const isUrgent = /(^|\|\s*)URGENTE\b/.test(request.notes || '');
  const materialSku = request.inventory_item?.sku;
  const projectTitle = request.project?.title || 'Sin proyecto';
  const projectNumber = request.project?.project_number;
  const taskTitle = request.task?.title;

  return (
    <>
      <Card className="material-request-card">
        <Card.Body>
          <div className="request-card-header">
            <div className="request-card-info">
              <div className="request-card-material">{materialName}</div>
              <div className="request-card-quantity">
                {esMultiItem
                  // Los materiales de verdad, en vez del relleno de la
                  // cabecera. Se listan los tres primeros y se cuenta el resto:
                  // el técnico necesita reconocer SU solicitud de un vistazo,
                  // no leerla entera.
                  ? resumenItems
                  : <>{quantity} {unitLabel}</>}
                {materialSku && (
                  <span style={{ marginLeft: '8px', color: '#0057B8' }}>
                    ({materialSku})
                  </span>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="request-card-status">
                <span className={`status-badge ${getStatusBadgeClass(request.status)}`}>
                  {MATERIAL_REQUEST_STATUS_LABELS[request.status] || request.status}
                </span>
              </div>
              <KebabMenu label={`Acciones de la solicitud de ${materialName}`} stopPropagation={false}>
                {request.project?.id && (
                  <Dropdown.Item as={Link} to={projectPath(request.project.id)}>
                    <i className="bi bi-folder me-2"></i>Ver Proyecto
                  </Dropdown.Item>
                )}
                {request.task?.id && (
                  <Dropdown.Item as={Link} to={taskPath(request.task.id)}>
                    <i className="bi bi-list-task me-2"></i>Ver Tarea
                  </Dropdown.Item>
                )}
                {isPending && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Item className="text-danger" onClick={() => setShowCancelModal(true)}>
                      <i className="bi bi-x-circle me-2"></i>Cancelar Solicitud
                    </Dropdown.Item>
                  </>
                )}
              </KebabMenu>
            </div>
          </div>

          <div className="request-card-details">
            <span className="request-card-detail">
              <i className="bi bi-folder"></i>
              {projectNumber ? `${projectNumber} - ` : ''}{projectTitle}
            </span>
            {taskTitle && (
              <span className="request-card-detail">
                <i className="bi bi-list-task"></i>
                {taskTitle}
              </span>
            )}
            <span className="request-card-detail">
              <i className="bi bi-calendar"></i>
              {formatDate(request.created_at)}
            </span>
            {isUrgent && (
              <span className="request-card-detail" style={{ color: '#dc3545' }}>
                <i className="bi bi-exclamation-triangle-fill"></i>
                Urgente
              </span>
            )}
          </div>

          {request.notes && (
            <div
              style={{
                fontSize: '0.8125rem',
                color: '#6c757d',
                fontStyle: 'italic',
                marginBottom: '12px',
              }}
            >
              "{request.notes}"
            </div>
          )}

          {/* Show rejection reason if rejected */}
          {request.status === MATERIAL_REQUEST_STATUS.REJECTED && request.rejection_reason && (
            <div
              style={{
                padding: '8px 12px',
                background: 'rgba(220, 53, 69, 0.08)',
                borderRadius: '8px',
                fontSize: '0.8125rem',
                // #581 — tinta de estado. `#dc3545` sobre este mismo tinte da
                // 4.04:1; medido en el DOM, no leído del CSS (un estilo inline
                // como éste no lo ve ningún barrido de .css).
                color: 'var(--estado-ink-rojo, #b02a37)',
                marginBottom: '12px',
              }}
            >
              <strong>Motivo de rechazo:</strong> {request.rejection_reason}
            </div>
          )}

          {/* Show approved info if approved */}
          {request.status === MATERIAL_REQUEST_STATUS.APPROVED && (
            <div
              style={{
                padding: '8px 12px',
                background: 'rgba(40, 167, 69, 0.08)',
                borderRadius: '8px',
                fontSize: '0.8125rem',
                // #581 — `#28a745` acá medía 2.88:1 sobre su propio tinte: el
                // peor de la tarjeta, y sólo lo encontró la medición del DOM.
                color: 'var(--estado-ink-verde, #146c43)',
                marginBottom: '12px',
              }}
            >
              <i className="bi bi-check-circle me-1"></i>
              Aprobada {request.reviewed_at && `el ${formatDateTime(request.reviewed_at)}`}
              {request.reviewed_by_profile && (
                <span> por {request.reviewed_by_profile.full_name}</span>
              )}
            </div>
          )}

          {/* Show delivered info if delivered */}
          {request.status === MATERIAL_REQUEST_STATUS.DELIVERED && (
            <div
              style={{
                padding: '8px 12px',
                background: 'rgba(0, 87, 184, 0.08)',
                borderRadius: '8px',
                fontSize: '0.8125rem',
                color: '#0057B8',
                marginBottom: '12px',
              }}
            >
              <i className="bi bi-truck me-1"></i>
              Entregada {request.delivered_at && `el ${formatDateTime(request.delivered_at)}`}
            </div>
          )}

          {isPending && (
            <div className="request-card-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setShowCancelModal(true)}
              >
                <i className="bi bi-x-circle me-1"></i>
                Cancelar Solicitud
              </button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Cancel Confirmation Modal */}
      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancelar Solicitud</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {esMultiItem
              // "¿cancelar la solicitud de 1 varios de Material desconocido?"
              // era la peor forma de esto: la pregunta que pide confirmar una
              // acción irreversible tiene que nombrar lo que se va a cancelar.
              ? <>Estas seguro de que deseas cancelar esta solicitud de{' '}
                <strong>{materialName}</strong>?</>
              : <>Estas seguro de que deseas cancelar la solicitud de{' '}
                <strong>{quantity} {unitLabel}</strong>{' '}
                de <strong>{materialName}</strong>?</>}
          </p>
          <p className="text-muted small mb-0">
            Esta accion no se puede deshacer.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowCancelModal(false)}
            disabled={isCancelling}
          >
            No, mantener
          </Button>
          <Button
            variant="danger"
            onClick={handleCancel}
            disabled={isCancelling}
          >
            {isCancelling ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Cancelando...
              </>
            ) : (
              <>
                <i className="bi bi-x-circle me-1"></i>
                Si, cancelar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

MaterialRequestCard.propTypes = {
  request: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    quantity_requested: PropTypes.number.isRequired,
    unit: PropTypes.string,
    notes: PropTypes.string,
    external_description: PropTypes.string,
    rejection_reason: PropTypes.string,
    created_at: PropTypes.string,
    reviewed_at: PropTypes.string,
    delivered_at: PropTypes.string,
    inventory_item: PropTypes.shape({
      name: PropTypes.string,
      sku: PropTypes.string,
      unit: PropTypes.string,
    }),
    project: PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      project_number: PropTypes.string,
    }),
    task: PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
    }),
    reviewed_by_profile: PropTypes.shape({
      full_name: PropTypes.string,
    }),
  }).isRequired,
  onCancelled: PropTypes.func,
};

export default MaterialRequestCard;
